import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import FeatherIcon from '../components/FeatherIcon';
import DonationCertificateCard from '../components/DonationCertificateCard';
import ShareDonation from '../components/ShareDonation';
import ReceiptActions from '../components/ReceiptActions';
import { apiBase } from '../lib/api';

/**
 * @description Donation session details from Stripe checkout
 * @interface SessionDetails
 * @api Retrieved from /api/donations/session/{sessionId} endpoint
 * @security Contains sensitive customer information
 */
interface SessionDetails {
  /** @description Stripe session ID for transaction reference */
  id: string;
  /** @description Total charged in cents (Stripe format), including any processing fee the donor covered */
  amount_total: number;
  /** @description The gift alone in cents — what reaches the foundation, without the covered fee.
   *  Null when the API cannot tell. */
  gift_amount?: number | null;
  /** @description The email entered at checkout. Stripe's copy never changes, so a corrected
   *  address comes from the correction response instead. */
  customer_email: string;
  /** @description Donor name from Stripe checkout */
  customer_name?: string | null;
  /** @description Timestamp when payment was completed */
  created?: number;
  /** @description Whether this gift repeats */
  recurring?: boolean;
  /** @description Whether the one-time email correction is still available */
  emailCorrectable?: boolean;
}

/**
 * @description Analytics event data structure
 * @interface AnalyticsEvent
 */
interface AnalyticsEvent {
  /** @description Event name for tracking */
  event: string;
  /** @description Event parameters */
  parameters: Record<string, unknown>;
}

/**
 * @description Live totals from GET /api/impact. `fundsRaised` is in dollars. `donorCount` is
 *   optional: the line names a donor count only once the API reports one.
 */
interface Impact {
  readonly fundsRaised: number;
  readonly donorCount?: number;
}

const formatUsd = (dollars: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);

/**
 * @description Thank you page component for successful donations
 * @component Displays donation confirmation and receipt information
 * @accessibility Fully keyboard navigable with ARIA labels and announcements
 * @responsive Mobile-first design with optimal tablet and desktop layouts
 * @security Validates session data and handles sensitive information appropriately
 * @performance Implements lazy loading and error boundaries for optimal UX
 * @analytics Tracks donation completion and user engagement events
 * @example
 * ```tsx
 * // Redirected from Stripe: /thank-you?session_id=cs_test_xxx
 * <Route path="/thank-you" element={<ThankYouPage />} />
 * ```
 * @since 1.0.0
 */
const ThankYouPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [impact, setImpact] = useState<Impact | null>(null);
  // Set by the one-time correction; Stripe's session keeps the address as typed at checkout.
  const [correctedEmail, setCorrectedEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');
  const receiptEmail = correctedEmail ?? sessionDetails?.customer_email;
  const MAX_RETRY_ATTEMPTS = 3;
  const RETRY_DELAY = 1000; // 1 second

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Live totals only: when the request fails, the line is simply not shown.
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${apiBase}/api/impact`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: Impact) => setImpact(data))
      .catch(() => setImpact(null));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails(sessionId);
      trackAnalyticsEvent({ event: 'donation_thank_you_view', parameters: {} });
    } else {
      setLoading(false);
      setError('No session ID provided. Please complete a donation first.');
    }
  }, [sessionId]);

  /**
   * @description Fetches donation session details with retry logic
   * @param sessionId - Stripe session ID to fetch details for
   * @throws Error when session cannot be retrieved after max retries
   * @api Calls GET /api/donations/session/{sessionId} endpoint
   * @performance Implements exponential backoff for failed requests
   */
  const fetchSessionDetails = useCallback(async (sessionId: string) => {
    try {
      const apiUrl = apiBase;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      const response = await fetch(`${apiUrl}/api/donations/session/${sessionId}`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Donation session not found. Please verify your payment was completed.');
        } else if (response.status >= 500) {
          throw new Error('Our servers are temporarily unavailable. Please try again.');
        }
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setSessionDetails(data);
      setError(null);

      trackAnalyticsEvent({
        event: 'donation_completed',
        parameters: { amount: data.amount_total / 100, currency: 'usd' }
      });

    } catch (err) {
      console.error('Failed to fetch session details:', err);

      if (retryCount < MAX_RETRY_ATTEMPTS) {
        const delay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchSessionDetails(sessionId);
        }, delay);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Unable to load donation details';
      setError(errorMessage);

      trackAnalyticsEvent({
        event: 'donation_details_error',
        parameters: { error: errorMessage, retry_count: retryCount }
      });
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  /**
   * @description Tracks analytics events for donation completion monitoring
   * @param event - Event data to track
   * @security Never pass the Checkout session id or the donor's email: the session id unlocks the
   *   receipt for whoever holds it, and neither belongs in a third-party analytics store.
   */
  const trackAnalyticsEvent = useCallback((event: AnalyticsEvent) => {
    // Google Analytics 4
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event.event, event.parameters);
    }

    // Facebook Pixel (if configured)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Purchase', {
        value: event.parameters.amount || 0,
        currency: 'USD'
      });
    }
  }, []);

  /**
   * @description Handles retry attempts for failed session fetching
   * @accessibility Announces retry attempts to screen readers
   */
  const handleRetry = useCallback(() => {
    if (sessionId && retryCount < MAX_RETRY_ATTEMPTS) {
      setLoading(true);
      setError(null);
      setRetryCount(prev => prev + 1);
      fetchSessionDetails(sessionId);
    }
  }, [sessionId, retryCount, fetchSessionDetails]);

  /**
   * @description Formats donation amount with proper currency display
   * @param amountCents - Amount in cents from Stripe
   * @returns Formatted currency string
   */
  const formatAmount = useCallback((amountCents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amountCents / 100);
  }, []);

  /**
   * @description Formats transaction date for display
   * @param timestamp - Unix timestamp from Stripe
   * @returns Formatted date string
   */
  const formatDate = useCallback((timestamp: number): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp * 1000));
  }, []);

  // Loading state with retry information and accessibility
  if (loading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center px-4"
        role="main"
        aria-live="polite"
      >
        <div className="text-center max-w-md">
          <div
            className={`${prefersReducedMotion ? '' : 'animate-spin'} rounded-full h-12 w-12 border-b-2 border-secondary mx-auto mb-4`}
            aria-label="Loading donation details"
            role="progressbar"
            aria-valuenow={Math.min(((retryCount + 1) / (MAX_RETRY_ATTEMPTS + 1)) * 100, 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          ></div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Loading your donation details...
          </h1>
          {retryCount > 0 && (
            <p className="text-sm text-gray-600" aria-live="polite">
              Attempt {retryCount + 1} of {MAX_RETRY_ATTEMPTS + 1}
            </p>
          )}
          <div className="mt-4 space-y-1">
            <div
              className="h-2 bg-gray-200 rounded-full overflow-hidden"
              role="progressbar"
              aria-label="Loading progress"
              aria-valuenow={Math.min(((retryCount + 1) / (MAX_RETRY_ATTEMPTS + 1)) * 100, 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-secondary transition-all duration-1000 ease-out"
                style={{ width: `${Math.min(((retryCount + 1) / (MAX_RETRY_ATTEMPTS + 1)) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500">
              Fetching your confirmation details from our secure servers
            </p>
          </div>
          {/* Skip to content for screen readers */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded-md z-50"
          >
            Skip to main content
          </a>
        </div>
      </div>
    );
  }

  // Enhanced error state with recovery options
  if (error && !sessionDetails) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center px-4"
        role="main"
        aria-live="assertive"
      >
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FeatherIcon name="alert-circle" size={32} className="text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Details</h1>
          <p className="text-gray-600 mb-6">{error}</p>

          <div className="space-y-3">
            {retryCount < MAX_RETRY_ATTEMPTS && sessionId && (
              <button
                onClick={handleRetry}
                className="w-full btn-primary py-3"
                aria-describedby="retry-description"
              >
                <FeatherIcon name="refresh-cw" size={16} className="mr-2" />
                Try Again ({MAX_RETRY_ATTEMPTS - retryCount} attempts left)
              </button>
            )}

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center mb-2">
                <FeatherIcon name="check-circle" size={20} className="text-green-600 mr-2" />
                <span className="font-semibold text-green-800">Your Payment Was Successful</span>
              </div>
              <p className="text-sm text-green-700">
                Don't worry! Your donation was processed successfully. You'll receive an email receipt shortly.
              </p>
            </div>

            <Link
              to="/"
              className="w-full border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors inline-flex items-center justify-center"
            >
              <FeatherIcon name="home" size={16} className="mr-2" />
              Return Home
            </Link>

            <p className="text-xs text-gray-500 mt-4">
              Having issues? Contact us at support@pawsitivefuture.org
            </p>
          </div>

          <div id="retry-description" className="sr-only">
            Clicking retry will attempt to reload your donation details from our servers
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center px-4 py-8"
      role="main"
      id="main-content"
    >
      <div className="max-w-2xl w-full bg-card rounded-xl shadow-xl p-5 sm:p-8 text-center relative overflow-hidden" itemScope itemType="https://schema.org/Invoice">
        {/* Success Animation Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-blue-50/30 pointer-events-none"></div>

        {/* Main Success Message */}
        <div className="relative z-10 mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <FeatherIcon name="check-circle" size={40} className="text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3" itemProp="description">
            Thank You for Your Generous Heart!
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-2" role="status" aria-live="polite">Your donation was successful</p>
          {sessionDetails?.customer_name && (
            <p className="text-sm text-green-600 font-medium" aria-label={`Welcome message for ${sessionDetails.customer_name}`}>
              Welcome to our community, {sessionDetails.customer_name}! 🎉
            </p>
          )}
        </div>

        {/* Certificate of Impact — the cool/modern in-site visual (mirrors the emailed/S3 PDF) */}
        {sessionDetails && (
          <div className="relative z-10 mb-6 sm:mb-8">
            <DonationCertificateCard
              amount={sessionDetails.amount_total / 100}
              donorName={sessionDetails.customer_name}
            />
          </div>
        )}

        {/* Enhanced Donation Details */}
        {sessionDetails && (
          <div className="relative z-10 mb-6 sm:mb-8">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-center mb-4">
                <FeatherIcon name="receipt" size={20} className="text-gray-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Your Donation Receipt</h2>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:gap-4 text-left max-w-md mx-auto">
                <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Donation Amount:</span>
                  <span className="text-2xl font-bold text-green-600" itemProp="totalPaymentDue" aria-label={`Donation amount ${formatAmount(sessionDetails.amount_total)}`}>
                    {formatAmount(sessionDetails.amount_total)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Receipt Email:</span>
                  <span className="min-w-0 break-all text-right font-semibold text-gray-900">
                    {receiptEmail}
                  </span>
                </div>

                {sessionDetails.created && (
                  <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200">
                    <span className="text-gray-600 font-medium">Date & Time:</span>
                    <span className="min-w-0 text-right font-semibold text-gray-900 text-sm">
                      {formatDate(sessionDetails.created)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 py-2">
                  <span className="text-gray-600 font-medium">Transaction ID:</span>
                  <span className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">
                    {sessionDetails.id.slice(-12)}
                  </span>
                </div>
              </div>

              {/* Tax Deductible Notice */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center justify-center mb-1">
                  <FeatherIcon name="info" size={16} className="text-yellow-600 mr-1" />
                  <span className="text-sm font-semibold text-yellow-800">Tax Deductible</span>
                </div>
                <p className="text-xs text-yellow-700 text-center">
                  Save this receipt for your tax records. No goods or services were provided in exchange for this donation.
                </p>
              </div>

              {sessionId && (
                <ReceiptActions
                  sessionId={sessionId}
                  emailCorrectable={Boolean(sessionDetails.emailCorrectable)}
                  recurring={Boolean(sessionDetails.recurring)}
                  onEmailCorrected={setCorrectedEmail}
                />
              )}
            </div>
          </div>
        )}

        {/* Right after the receipt: the moment a donor is most likely to post about it. */}
        <div className="relative z-10 mb-6 sm:mb-8">
          <ShareDonation
            // The gift, not the charge: a covered processing fee never reaches the foundation, so it
            // isn't part of what a donor announces. Without gift_amount the amount option is hidden.
            amountCents={sessionDetails?.gift_amount ?? undefined}
            onShare={(platform) => trackAnalyticsEvent({ event: 'donation_social_share', parameters: { platform } })}
          />
        </div>

        {/* Impact Message & Receipt Notice */}
        <div className="relative z-10 mb-8">
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 mb-6 border border-green-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center justify-center">
              <FeatherIcon name="heart" size={20} className="text-red-500 mr-2" />
              Your Impact in Action
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your generous contribution of <strong>{sessionDetails ? formatAmount(sessionDetails.amount_total) : 'your donation'}</strong> will
              directly fund essential veterinary care for pets whose families are struggling financially.
              You've just helped us continue our mission to ensure that love, not financial hardship,
              determines a pet's access to lifesaving medical care.
            </p>

            {/* Live totals from /api/impact — nothing is shown rather than a stale or invented figure. */}
            {impact && (
              <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-800 text-center font-medium">
                  🌟 So far,{' '}
                  {typeof impact.donorCount === 'number'
                    ? <strong>{impact.donorCount.toLocaleString('en-US')} {impact.donorCount === 1 ? 'donor has' : 'donors have'}</strong>
                    : 'donors have'}{' '}
                  given <strong>{formatUsd(impact.fundsRaised)}</strong> to Pawsitive Future.
                </p>
              </div>
            )}
          </div>

          {/* Enhanced Receipt Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-center mb-3">
              <FeatherIcon name="mail" size={20} className="text-blue-600 mr-2" />
              <span className="font-semibold text-blue-900">Your Receipt is On Its Way</span>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-blue-800 text-center">
                Your official tax-deductible receipt will be emailed to <strong className="break-all">{receiptEmail || 'you'}</strong> within the next few minutes.
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-blue-700 mt-3">
                <div className="flex items-center">
                  <FeatherIcon name="clock" size={12} className="mr-1" />
                  <span>2-5 minutes</span>
                </div>
                <div className="w-px h-4 bg-blue-300"></div>
                <div className="flex items-center">
                  <FeatherIcon name="shield-check" size={12} className="mr-1" />
                  <span>Secure delivery</span>
                </div>
              </div>
              <p className="text-xs text-blue-600 text-center mt-2">
                💡 <strong>Pro tip:</strong> Check your spam/junk folder if you don't see it in your inbox
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Action Buttons */}
        <div className="relative z-10 space-y-4">
          {/* Primary Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/"
              className={`btn-primary inline-flex items-center justify-center py-4 px-6 rounded-xl font-semibold hover:shadow-lg ${prefersReducedMotion ? '' : 'transform hover:scale-105'} transition-all duration-200`}
              aria-label="Return to homepage"
            >
              <FeatherIcon name="home" size={20} className="mr-2" />
              Return Home
            </Link>

            <Link
              to="/donate"
              className={`border-2 border-green-600 text-green-600 px-6 py-4 rounded-xl font-semibold hover:bg-green-50 hover:shadow-lg ${prefersReducedMotion ? '' : 'transform hover:scale-105'} transition-all duration-200 inline-flex items-center justify-center relative`}
              aria-label="Make another donation to help more pets"
              onClick={() => trackAnalyticsEvent({
                event: 'donation_repeat_intent',
                parameters: { source: 'thank_you_page' }
              })}
            >
              <FeatherIcon name="heart" size={20} className="mr-2" />
              Give Again
            </Link>
          </div>

          {/* Newsletter Signup CTA */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-2 text-center">
              📬 Stay Connected with Our Mission
            </h3>
            <p className="text-sm text-gray-600 text-center mb-3">
              Get updates on the pets you're helping and upcoming volunteer opportunities.
            </p>
            {/* The newsletter form lives on /contact; there is no /newsletter route. */}
            <button
              type="button"
              className="w-full btn-accent px-4 py-3 rounded-lg font-medium transition-all duration-200 inline-flex items-center justify-center shadow-md hover:shadow-lg"
              aria-label="Subscribe to our newsletter for updates"
              onClick={() => {
                trackAnalyticsEvent({ event: 'newsletter_signup_intent', parameters: { source: 'thank_you_page' } });
                navigate('/contact');
              }}
            >
              <FeatherIcon name="mail" size={16} className="mr-2" />
              Join Our Community
            </button>
          </div>

          {/* Legal Notice */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 leading-relaxed">
              🏛️ <strong>Pawsitive Future Foundation</strong> is a 501(c)(3) nonprofit organization (EIN: 39-4222687).<br />
              Your donation is tax-deductible to the full extent allowed by law.
              <br /><br />
              🔒 <strong>Secure & Private:</strong> Your payment information is processed securely by Stripe and is never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;